import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, Calendar, User, LogOut, Download } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAddToHomeScreen } from '../hooks/useAddToHomeScreen';
import { isFeatureEnabled } from '../config/features';

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { user, signOut } = useAuth();
  const { isInstallable, promptInstall } = useAddToHomeScreen();
  const location = useLocation();
  const navigate = useNavigate();

  const isShoppingListsActive = location.pathname.startsWith('/shopping') || location.pathname === '/' || location.pathname.startsWith('/list/');
  const isTaskPlanningActive = location.pathname.startsWith('/tasks');

  const handleTabClick = (tab: 'shopping' | 'tasks') => {
    if (tab === 'shopping') {
      navigate('/shopping');
    } else if (tab === 'tasks') {
      navigate('/tasks');
    }
  };

  const handleSignOut = () => {
    signOut();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3 min-w-0 flex-shrink-0">
              <img src="/favicon-32.png" alt="" className="w-8 h-8 flex-shrink-0" width={32} height={32} />
              <h1 className="text-xl font-bold text-gray-900 sm:text-2xl truncate">UsSync.app</h1>
            </div>
            
            <div className="flex items-center gap-2 min-w-0">
              <div className="flex items-center gap-2 text-sm text-gray-600 min-w-0 flex-1 sm:flex-initial">
                <User className="w-4 h-4 flex-shrink-0" />
                <span className="truncate max-w-[120px] sm:max-w-[180px]" title={user?.full_name || user?.email || ''}>
                  {user?.full_name || user?.email}
                </span>
              </div>
              {isInstallable && (
                <button
                  onClick={promptInstall}
                  className="flex items-center justify-center w-9 h-9 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors flex-shrink-0"
                  title="Add to Home Screen"
                >
                  <Download className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={handleSignOut}
                className="flex items-center justify-center w-9 h-9 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors flex-shrink-0"
                title="Sign out"
                aria-label="Sign out"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 pb-20">
        {children}
      </main>

      {/* Bottom Tab Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center ${isFeatureEnabled('tasks') ? 'justify-around' : 'justify-center'} h-16`}>
            {/* Shopping Lists Tab */}
            <button
              onClick={() => handleTabClick('shopping')}
              className={`flex flex-col items-center justify-center space-y-1 px-4 py-2 rounded-lg transition-colors ${
                isShoppingListsActive
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <ShoppingCart className={`w-6 h-6 ${isShoppingListsActive ? 'text-blue-600' : 'text-gray-500'}`} />
              <span className="text-xs font-medium">Shopping</span>
            </button>

            {/* Task Planning Tab - Only show if feature is enabled */}
            {isFeatureEnabled('tasks') && (
              <button
                onClick={() => handleTabClick('tasks')}
                className={`flex flex-col items-center justify-center space-y-1 px-4 py-2 rounded-lg transition-colors ${
                  isTaskPlanningActive
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Calendar className={`w-6 h-6 ${isTaskPlanningActive ? 'text-blue-600' : 'text-gray-500'}`} />
                <span className="text-xs font-medium">Tasks</span>
              </button>
            )}
          </div>
        </div>
      </nav>
    </div>
  );
};

export default MainLayout;
